import type { LogPayload } from "./types";

import { datadogEnv } from "./datadog-env";
import { formatStructuredConsoleFallback } from "./format-structured-console-fallback";
import { getTagsFromPayload } from "./get-tags-from-payload";

function getMessageContext(payload: LogPayload): Record<string, unknown> {
  const { environment, service } = datadogEnv();
  const ddtags = getTagsFromPayload(payload);
  const resolvedService = payload.service ?? service;

  return {
    ...(payload.data ?? {}),
    ...(ddtags ? { ddtags } : {}),
    ...(environment ? { env: environment } : {}),
    ...(resolvedService ? { service: resolvedService } : {}),
  };
}

function logStructuredToConsole(payloads: LogPayload[]): void {
  console.warn(
    "Datadog browser logging unavailable. Logging to console instead:",
    formatStructuredConsoleFallback(payloads),
  );
}

function emitPayloads(
  datadogLogs: Awaited<typeof import("@datadog/browser-logs")>["datadogLogs"],
  payloads: LogPayload[],
): void {
  for (const payload of payloads) {
    datadogLogs.logger.log(
      payload.message,
      getMessageContext(payload),
      payload.level ?? "info",
    );
  }
}

async function emitBrowserLogs(payloads: LogPayload[]): Promise<void> {
  if (payloads.length === 0) {
    return;
  }

  const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN ?? "";
  if (!clientToken) {
    logStructuredToConsole(payloads);
    return;
  }

  try {
    const { datadogLogs } = await import("@datadog/browser-logs");

    datadogLogs.onReady(() => {
      emitPayloads(datadogLogs, payloads);
    });
  } catch {
    logStructuredToConsole(payloads);
  }
}

export { emitBrowserLogs };
